import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { app as teamsApp } from '@microsoft/teams-js';
import { App } from './App';
import { loginRequest, msalInstance } from './auth/msalConfig';
import { notifyTeamsSuccess } from './auth/teamsSignin';
import { reportNonFatal } from './observability/reportNonFatal';
import './styles/global.css';

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Missing #root element in index.html');

type PopupState =
  | { kind: 'progress' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

function popupRender(state: PopupState): void {
  if (!rootElement) return;
  const headline =
    state.kind === 'success'
      ? '✔ Kirjautuminen onnistui'
      : state.kind === 'error'
        ? '✖ Kirjautumisvirhe'
        : 'Kirjaudutaan…';
  const subtext =
    state.kind === 'success'
      ? 'Voit nyt sulkea tämän ikkunan. Palaa Teams-välilehdelle jatkaaksesi.'
      : state.kind === 'error'
        ? state.message
        : 'Yhdistetään Microsoft-tiliin.';
  // DOM APIs, not innerHTML: `subtext` in the error case is the raw MSAL
  // err.message — we must not treat it as HTML.
  rootElement.replaceChildren();
  const container = document.createElement('div');
  container.style.cssText =
    'padding:36px;font-family:system-ui,sans-serif;color:#222;max-width:520px;';

  const brand = document.createElement('div');
  brand.style.cssText = 'font-size:18px;font-weight:700;margin-bottom:18px;';
  brand.textContent = "MS AI What's New";

  const headlineEl = document.createElement('div');
  headlineEl.style.cssText = `font-size:20px;font-weight:700;margin-bottom:8px;color:${state.kind === 'error' ? '#b00020' : '#222'};`;
  headlineEl.textContent = headline;

  const subtextEl = document.createElement('div');
  subtextEl.style.cssText = 'font-size:14px;color:#444;margin-bottom:16px;';
  subtextEl.textContent = subtext;

  const btn = document.createElement('button');
  btn.style.cssText =
    'padding:8px 18px;border-radius:6px;border:none;background:#0078D4;color:#fff;cursor:pointer;font-weight:600;';
  btn.textContent = 'Sulje ikkuna';
  btn.onclick = () => window.close();
  const btnWrap = document.createElement('div');
  btnWrap.appendChild(btn);

  container.append(brand, headlineEl, subtextEl, btnWrap);
  rootElement.appendChild(container);
}

// We detect the popup role via both a URL param (first hit) and
// sessionStorage (after the Entra round-trip, which strips our query string).
// sessionStorage is scoped to this window, so it persists through the
// login.microsoftonline.com navigation but does NOT leak to other tabs.
//
// The flag also carries a timestamp so a crashed popup can't turn the main
// tab into a permanent-popup zombie on a subsequent open. Anything older
// than STALE_POPUP_FLAG_MS is treated as stale and cleared.
const TEAMS_POPUP_FLAG = 'ms-ai-whats-new:teams-popup';
const STALE_POPUP_FLAG_MS = 5 * 60 * 1000;

function isPopupFlagActive(): boolean {
  const raw = sessionStorage.getItem(TEAMS_POPUP_FLAG);
  if (!raw) return false;
  // Legacy value was just '1' — treat that as active too so a mid-upgrade
  // popup doesn't silently break. Otherwise parse {ts}.
  if (raw === '1') return true;
  try {
    const parsed = JSON.parse(raw) as { ts?: number };
    if (typeof parsed.ts !== 'number') {
      sessionStorage.removeItem(TEAMS_POPUP_FLAG);
      return false;
    }
    if (Date.now() - parsed.ts > STALE_POPUP_FLAG_MS) {
      sessionStorage.removeItem(TEAMS_POPUP_FLAG);
      return false;
    }
    return true;
  } catch (err) {
    // sessionStorage or JSON.parse blew up — rare, but worth seeing if it
    // happens so we can spot flag corruption.
    reportNonFatal(err, 'popup.flag-parse');
    sessionStorage.removeItem(TEAMS_POPUP_FLAG);
    return false;
  }
}

const urlParamPresent = new URLSearchParams(window.location.search).has('teams-popup');
if (urlParamPresent) {
  sessionStorage.setItem(TEAMS_POPUP_FLAG, JSON.stringify({ ts: Date.now() }));
}
const isTeamsAuthPopup = isPopupFlagActive();

function closePopupFallback(): void {
  // Belt-and-braces: if Teams' message channel was severed by the
  // cross-origin redirect to login.microsoftonline.com, notifySuccess may not
  // close the window. Try window.close() after a short delay so the user
  // isn't stuck staring at "Kirjaudutaan...".
  setTimeout(() => {
    try {
      window.close();
    } catch (err) {
      // Cross-origin policy sometimes blocks a script-initiated close —
      // the user closes the tab themselves. Worth a beacon so we can
      // count how often this fallback fires.
      reportNonFatal(err, 'popup.window-close');
    }
  }, 600);
}

async function runTeamsAuthPopup(): Promise<void> {
  // Teams SDK initialize() rejects in this popup context; notifySuccess
  // therefore can't run. MSAL itself still processes the redirect fine,
  // writing tokens to localStorage where the main tab picks them up via
  // polling. The popup then shows an explicit success screen + close button.
  try {
    await teamsApp.initialize();
  } catch (err) {
    // Expected in the auth popup (Teams SDK has no host to handshake with
    // across the cross-origin redirect). Routing through reportNonFatal
    // mostly for the pattern's sake — filter this context out in KQL if
    // the noise outweighs the signal.
    reportNonFatal(err, 'popup.teams-init-expected');
  }

  // Reach code paths that terminate without the flag cleared (unhandled
  // rejection, browser closing mid-navigation, etc.) would leave the flag
  // behind and cause the next tab load to boot into popup-mode permanently.
  // `redirectStarted` tells the finally-block to keep the flag only if we're
  // about to leave for login.microsoftonline.com (where it'll persist
  // through the redirect and be cleared on the return trip).
  let redirectStarted = false;
  try {
    await msalInstance.initialize();

    const existing = msalInstance.getAllAccounts();
    if (existing.length > 0) {
      try {
        notifyTeamsSuccess('signed-in');
      } catch (err) {
        // Teams bridge severed by the cross-origin redirect — main tab
        // polling will see the new token regardless. Instrumented so we
        // can measure how often the fallback path carries auth.
        reportNonFatal(err, 'popup.notify-teams-success-existing');
      }
      popupRender({ kind: 'success' });
      closePopupFallback();
      return;
    }

    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      try {
        notifyTeamsSuccess('signed-in');
      } catch (err) {
        // Same fallback as above, after the redirect leg completed.
        reportNonFatal(err, 'popup.notify-teams-success-redirect');
      }
      popupRender({ kind: 'success' });
      closePopupFallback();
      return;
    }

    redirectStarted = true;
    await msalInstance.loginRedirect(loginRequest);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    // Don't fire notifyTeamsFailure here — it errors because the bridge is
    // down, and we've already rendered a friendly error state.
    popupRender({ kind: 'error', message });
  } finally {
    // Always clear the flag on terminal outcomes (success, handled error).
    // The only case we must NOT clear is mid-redirect to Entra, since the
    // flag needs to survive the navigation; the return trip lands in one of
    // the happy-path branches above and clears it then.
    if (!redirectStarted) {
      sessionStorage.removeItem(TEAMS_POPUP_FLAG);
    }
  }
}

if (isTeamsAuthPopup) {
  popupRender({ kind: 'progress' });
  void runTeamsAuthPopup();
} else {
  createRoot(rootElement).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
